import { useMutation } from '@apollo/client';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Body, Button, Input, Modal, Select, SelectOption, useFlashMessage } from '@indigo-ag/component-module';
import { FinancingRole, Organization } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { inviteNewUser } from 'features/admin/adminQueries';
import React, { useState } from 'react';
import { EnhancedPaper, createStylesHook, text } from 'shared';

const SIMPLE_EMAIL_REGEXP = new RegExp(/\S+@\S+\.\S+/);

const useStyles = createStylesHook(theme => ({
  input: {
    '& label': {
      color: '#5B6B72',
      padding: '0 0 0.25rem 0'
    },
    '& div[role=group]': {
      margin: '0 10px 0 0',
      fontSize: theme.typography.pxToRem(14),
      border: '1px solid #ACC0CA',
      height: '48px',
      borderRadius: '4px',
      '& input': {
        fontWeight: 'normal',
        fontSize: '16px',
        '&:placeholder': {
          color: '#899CA5'
        }
      }
    }
  },
  customSelect: {
    margin: 0,
    '& label': {
      color: '#5B6B72',
      padding: '0 0 0.25rem 0'
    },
    '& select': {
      height: '48px',
      borderRadius: '4px',
      color: '#16191B',
      border: '1px solid #ACC0CA'
    }
  },
  mr1dot5: {
    marginRight: '1.5rem'
  },
  dInlineBlock: {
    display: 'inline-block'
  }
}));

interface AddUserProps {
  roles: FinancingRole[];
  organizations: Organization[];
}

export const AddNewUser: React.FunctionComponent<AddUserProps> = ({ roles, organizations }) => {
  const classes = useStyles();
  const [createNewUser] = useMutation(inviteNewUser);
  const [roleType, setRoleType] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [familyName, setFamilyName] = useState<string>('');
  const [givenName, setGivenName] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  const [roleTypeError, setRoleTypeError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [familyNameError, setFamilyNameError] = useState<string | null>(null);
  const [givenNameError, setGivenNameError] = useState<string | null>(null);
  const [organizationError, setOrganizationError] = useState<string | null>(null);

  const selectableOrgs: SelectOption[] = organizations.map(org => ({
    name: org.name ?? '',
    value: org.id
  }));

  const selectableRoles: SelectOption[] = roles.map(role => ({
    name: role.name,
    value: role.id
  }));
  const [openFirst, setOpenFirst] = React.useState(false);

  const { addFlashMessage } = useFlashMessage();

  const clearErrors = () => {
    setRoleTypeError(null);
    setEmailError(null);
    setFamilyNameError(null);
    setGivenNameError(null);
    setOrganizationError(null);
  };

  const clearInputs = () => {
    setEmail('');
    setFamilyName('');
    setGivenName('');
    setOrganizationId(null);
    setRoleType(null);
  };

  const cancel = () => {
    clearInputs();
    clearErrors();
    setOpenFirst(false);
  };

  const validateFields = (): boolean => {
    let valid = true;
    if (!email) {
      valid = false;
      setEmailError(i18next.t('inputFieldRequiredError'));
    }
    if (!SIMPLE_EMAIL_REGEXP.test(email)) {
      valid = false;
      setEmailError(i18next.t('invalidEmailAddress'));
    }
    if (!givenName) {
      valid = false;
      setGivenNameError(i18next.t('inputFieldRequiredError'));
    }
    if (!familyName) {
      valid = false;
      setFamilyNameError(i18next.t('inputFieldRequiredError'));
    }
    if (!organizationId) {
      valid = false;
      setOrganizationError(i18next.t('inputFieldRequiredError'));
    }
    if (!roleType) {
      valid = false;
      setRoleTypeError(i18next.t('inputFieldRequiredError'));
    }
    if (organizationId === 'ind1g0' && !/.+@indigoag.com$/i.test(email)) {
      valid = false;
      setEmailError(i18next.t('invalidEmailAddressForIndigo'));
    }
    return valid;
  };

  const submit = () => {
    clearErrors();
    if (validateFields()) {
      createNewUser({
        variables: {
          email,
          givenName,
          familyName,
          organizationId,
          role: roleType
        }
      })
        .then(() => {
          addFlashMessage({
            content: i18next.t('userUpdated'),
            title: '',
            variant: 'success'
          });
        })
        .catch(() => {
          addFlashMessage({
            content: i18next.t('error'),
            title: '',
            variant: 'error'
          });
        })
        .finally(() => {
          clearInputs();
          setOpenFirst(false);
        });
    }
  };
  return (
    <EnhancedPaper
      style={{
        marginBottom: '20px',
        paddingBottom: '7px',
        boxShadow: '0px 2px 4px rgba(0, 87, 125, 0.14), 0px 2px 2px rgba(0, 87, 125, 0.1)'
      }}
    >
      <div
        style={{
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Button
          onClick={() => {
            setOpenFirst(true);
          }}
        >
          {text.toTitleCase(i18next.t('createNewUser'))}
        </Button>
        <Modal
          actionButtons={[
            {
              onClick: submit,
              label: i18next.t('accept'),
              variant: 'primary',
              faIcon: faCheck
            },
            {
              onClick: cancel,
              label: i18next.t('cancel'),
              variant: 'secondary',
              faIcon: faTimes
            }
          ]}
          analyticsKey="create-user-modal"
          open={openFirst}
          onRequestClose={() => {
            setOpenFirst(false);
          }}
          renderContent={() => (
            <>
              <Body>
                <form>
                  <Input
                    label={i18next.t('email')}
                    className={classes.input}
                    placeholder="name@email.com"
                    onChange={e => setEmail(e.target.value)}
                    value={email}
                    variant="email"
                    sizeVariant="slim"
                    error={emailError}
                  />
                  <Input
                    label={i18next.t('givenName')}
                    className={classes.input}
                    placeholder="João"
                    onChange={e => setGivenName(e.target.value)}
                    value={givenName}
                    sizeVariant="slim"
                    error={givenNameError}
                  />
                  <Input
                    label={i18next.t('familyName')}
                    className={classes.input}
                    placeholder="da Silva"
                    onChange={e => setFamilyName(e.target.value)}
                    value={familyName}
                    sizeVariant="slim"
                    error={familyNameError}
                  />
                  <Select
                    className={classes.customSelect}
                    label={i18next.t('organization')}
                    name="org-select"
                    placeholder={i18next.t('pleaseSelect')}
                    value={organizationId}
                    onChange={e => setOrganizationId(e.target.value)}
                    sizeVariant="slim"
                    options={selectableOrgs}
                    error={organizationError}
                  />
                  <Select
                    className={classes.customSelect}
                    label={i18next.t('role')}
                    name="role-select"
                    placeholder={i18next.t('pleaseSelect')}
                    value={roleType}
                    onChange={e => setRoleType(e.target.value)}
                    sizeVariant="slim"
                    options={selectableRoles}
                    error={roleTypeError}
                  />
                </form>
              </Body>
            </>
          )}
          title={i18next.t('createNewUser')}
        />
      </div>
    </EnhancedPaper>
  );
};
